import {
	createActionsHook,
	createHook,
	createStore,
	type BoundActions,
	type HookActionsFunction,
	type StoreActionApi,
} from 'react-sweet-state';

const MOBILE_BREAKPOINT = 480;
const TABLET_BREAKPOINT = 768;
const SMALL_DESKTOP_BREAKPOINT = 1024;
const NARROW_BREAKPOINT = 960;

type LayoutStoreState = {
	containerWidth: number | null;
};

const initialState: LayoutStoreState = {
	containerWidth: null,
};

const actions = {
	setContainerWidth:
		(containerWidth: number): (({ setState }: StoreActionApi<LayoutStoreState>) => void) =>
		({ setState }: StoreActionApi<LayoutStoreState>) => {
			setState({ containerWidth });
		},
} as const;

type Actions = typeof actions;

const LayoutStore = createStore<LayoutStoreState, Actions>({
	initialState,
	actions,
	name: 'LayoutStore',
});

const useLayoutStoreInner = createHook(LayoutStore);

const useIsSmallDesktopOrSmallerInner = createHook(LayoutStore, {
	selector: (state) => !!(state.containerWidth && state.containerWidth < SMALL_DESKTOP_BREAKPOINT),
});

const useIsLargeDesktopOrSmallerInner = createHook(LayoutStore, {
	selector: (state) => !!(state.containerWidth && state.containerWidth < 1767),
});

const useIsTabletOrSmallerInner = createHook(LayoutStore, {
	selector: (state) => !!(state.containerWidth && state.containerWidth < TABLET_BREAKPOINT),
});

const useIsMobileInner = createHook(LayoutStore, {
	selector: (state) => !!(state.containerWidth && state.containerWidth < MOBILE_BREAKPOINT),
});

const useIsNarrowInner = createHook(LayoutStore, {
	selector: (state) => !!(state.containerWidth && state.containerWidth < NARROW_BREAKPOINT),
});

export const useLayoutStore = (): LayoutStoreState => useLayoutStoreInner()[0];
export const useLayoutStoreActions: HookActionsFunction<
	BoundActions<
		LayoutStoreState,
		{
			readonly setContainerWidth: (
				containerWidth: number,
			) => ({ setState }: StoreActionApi<LayoutStoreState>) => void;
		}
	>
> = createActionsHook(LayoutStore);
export const useIsSmallDesktopOrSamller = (): boolean => useIsSmallDesktopOrSmallerInner()[0];
export const useIsLargeDesktopOrSmaller = (): boolean => useIsLargeDesktopOrSmallerInner()[0];
export const useIsTabletOrSmaller = (): boolean => useIsTabletOrSmallerInner()[0];
export const useIsLayoutNarrow = (): boolean => useIsNarrowInner()[0];
export const useIsMobile = (): boolean => useIsMobileInner()[0];
